/* eslint-disable prettier/prettier */
import { useEffect, useMemo, useState } from 'react';

import {
  Highlight,
  Post,
  PrismicCategoryCityPage,
  PrismicCategoryPage,
  Rating,
  Tree,
} from '@domain/entities';

import { PrismicDocument } from '@domain/types';
import * as prismic from '@prismicio/client';

import { Seo } from '@hoa/atoms';
import { Layout } from '@hoa/layouts';

import { useQueryParam, useTrade } from '@hoa/hooks';

import { TradeUrlSchema } from '@hoa/schema';

import { LocaleType } from '@hoa/types';

import Faq from '@hoa/components/organisms/Faq';
import { CategoryBlog } from './CategoryBlog';
import { CategoryCarousel } from './CategoryCarousel/CategoryCarousel';
import CategoryHero from './CategoryHero';
import { CategoryHighlights } from './CategoryHighlights';
import { CategoryPreFooter } from './CategoryPreFooter';
import { CategoryRating } from './CategoryRating';
import { CategorySubCategories } from './CategorySubCategories';
import { CategoryWhyJaimy } from './CategoryWhyJaimy';

export interface Props {
  lang: LocaleType | undefined;
  highlight: Highlight | undefined;
  trade?: string;
  rating: Rating | undefined;
  tree: Tree | undefined;
  categoryPage?: PrismicDocument<PrismicCategoryPage> | null;
  categoryCityPage?: PrismicDocument<PrismicCategoryCityPage> | null;
}

export const Category = (props: Props) => {
  const queryParam = useQueryParam<{ categoryId: string }>();
  const { trade, tradeQuoteUrl } = useTrade();
  const [categoryId, setCategoryId] = useState<string>(``);

  const questions = useMemo(
    () => props.categoryPage?.data.questions,
    [props.categoryPage, queryParam],
  );
  const heroDetails = useMemo(
    () =>
      props.categoryPage?.data.body.find(
        slice => slice.slice_type === 'hero_with_form',
      ),
    [props.categoryPage, queryParam],
  );

  const carouselDetails = useMemo(
    () =>
      props.categoryPage?.data.body.filter(
        slice => slice.slice_type === 'cards_carousel',
      ),
    [props.categoryPage, queryParam],
  );

  const posts: Post[] = useMemo(() => {
    if (!props.categoryPage) return [];
    const blogPosts = props.categoryPage.data.body.find(
      slice => slice.slice_type === 'blog_posts',
    );
    return blogPosts ? (blogPosts.items as unknown as Post[]) : [];
  }, [props.categoryPage, queryParam]);

  const subCategoriesDetails = useMemo(
    () =>
      props.categoryPage?.data.body.find(
        slice => slice.slice_type === 'sub_categories_section',
      ),
    [props.categoryPage, queryParam],
  );

  const preFooterDetails = useMemo(
    () =>
      props.categoryPage?.data.body.find(
        slice => slice.slice_type === 'pre_footer',
      ),
    [props.categoryPage, queryParam],
  );

  useEffect(() => {
    if (queryParam.categoryId) setCategoryId(queryParam.categoryId);
  }, []);

  const alternativeLinks = trade
    ? Object.entries(TradeUrlSchema).flatMap(([locale, value]) =>
        Object.entries(value)
          .filter(entry => entry?.[0] === trade)
          .map(value => ({
            locale,
            path: value?.[1],
          })),
      )
    : [];

  return (
    <Layout
      alternativeLinks={alternativeLinks}
      hasHeader={true}
      hasFooter={'small'}
    >
      {props.categoryPage && (
        <Seo
          title={props.categoryPage?.data.seo_title}
          description={props.categoryPage.data.description}
          indexed={props.categoryPage.data.page_indexed || false}
          // image={backgroundImage}
          // alternativeLinks={alternativeLinks}
        />
      )}
      {heroDetails && (
        <CategoryHero
          backgroundImage={heroDetails.primary.hero_image.url}
          title={heroDetails.primary.hero_title}
          subtitle={heroDetails.primary.subtitle}
          ctaButtonText={heroDetails.primary.hero_action_button_text}
        />
      )}
      <CategoryHighlights highlight={props.highlight} />
      {carouselDetails && (
        <CategoryCarousel servicesCarousels={carouselDetails} />
      )}
      {!categoryId && (
        <CategorySubCategories
          tree={props.tree}
          details={subCategoriesDetails}
        />
      )}
      <CategoryRating rating={props.rating} />
      {questions && questions.length > 0 && <Faq questions={questions} />}
      <CategoryWhyJaimy />
      {posts && posts.length > 0 && <CategoryBlog posts={posts} />}
      {preFooterDetails && (
        <CategoryPreFooter
          title={prismic.asText(preFooterDetails.primary.pre_footer_title)}
          subTitle={prismic.asText(
            preFooterDetails.primary.pre_footer_sub_title,
          )}
          button={preFooterDetails.primary.pre_footer_cta}
          url={tradeQuoteUrl}
        />
      )}
    </Layout>
  );
};
